import React, { useEffect, useRef, useState } from 'react';
import { DeleteOutline, EditOutlined, ExpandMore } from '@mui/icons-material';

import { LUT_GROUPS, css, hexToRgb, lutColor, lutLabel, rgbToHex } from '../Viewer/lut';

// Prépare un canvas à la taille affichée (écrans Retina compris).
function fitCanvas(canvas) {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.round(canvas.clientWidth * dpr);
  canvas.height = Math.round(canvas.clientHeight * dpr);
  return canvas.getContext('2d');
}

function DeleteButton({ name, onDelete }) {
  return (
    <button type="button" className="channel-delete" onClick={onDelete} title="Delete this modality"
      aria-label={`Delete modality ${name}`}>
      <DeleteOutline fontSize="inherit" />
    </button>
  );
}

const percent = v => `${Math.round(v * 100)}%`;

// Nom de la modalité : clic pour afficher / masquer ses réglages (carte repliée par défaut) ; sans onCollapse
// (présentation, lecture seule) : nom seul. onRename (contributeurs) : crayon au survol, nom modifiable sur place
// (Entrée ou sortie du champ : enregistré ; Échap : annulé ; vide : nom par défaut).
export function CardTitle({ name, collapsed, onCollapse, onRename }) {
  const [draft, setDraft] = useState(null);  // nom en cours de saisie, null hors renommage
  const editing = useRef(false);
  const finish = commit => {
    if (!editing.current) return;
    editing.current = false;
    const next = draft.trim();
    setDraft(null);
    if (commit && next !== name) onRename(next);
  };
  if (draft != null) {
    return (
      <input className="channel-rename" value={draft} maxLength={80} aria-label={`New name for ${name}`} autoFocus
        onFocus={e => e.target.select()} onChange={e => setDraft(e.target.value)} onBlur={() => finish(true)}
        onKeyDown={e => {
          if (e.key === 'Enter') finish(true);
          if (e.key === 'Escape') finish(false);
        }} />
    );
  }
  if (!onCollapse) return <span className="channel-name static" title={name}><span>{name}</span></span>;
  return (
    <>
      <button type="button" className="channel-name" onClick={onCollapse} aria-expanded={!collapsed}
        title={`${name} · ${collapsed ? 'show' : 'hide'} settings`}>
        <ExpandMore className={`channel-chevron${collapsed ? ' collapsed' : ''}`} fontSize="inherit" />
        <span>{name}</span>
      </button>
      {onRename && (
        <button type="button" className="channel-edit" title="Rename" aria-label={`Rename ${name}`}
          onClick={() => { editing.current = true; setDraft(name); }}>
          <EditOutlined fontSize="inherit" />
        </button>
      )}
    </>
  );
}

// Curseur simple (min → max), valeur affichée par format (pourcentage par défaut) ; double-clic : valeur par défaut.
export function Slider({ label, value, min = 0, max, step = 0.01, reset, format = percent, onChange }) {
  return (
    <>
      <div className="range" onDoubleClick={() => onChange(reset)} title="Double-click: default value">
        <div className="range-fill" style={{ left: 0, width: `${((value - min) / (max - min)) * 100}%` }} />
        <input type="range" min={min} max={max} step={step} value={value} aria-label={label}
          onChange={e => onChange(Number(e.target.value))} />
      </div>
      <div className="range-values"><span>{label}</span><span>{format(value)}</span></div>
    </>
  );
}

// Partie de la LUT utilisée pour le rendu ([début, fin] dans [0, 1]) : les deux bouts de la palette sont retirés, la
// partie gardée couvre toute la plage affichée. Barre : la LUT entière, bouts retirés assombris. Pas pour une couleur
// unie.
export function LutRange({ lut, range, onChange }) {
  const [a, b] = range || [0, 1];
  const full = `linear-gradient(to right, ${Array.from({ length: 33 }, (_, i) => css(lutColor(lut, [255, 255, 255], false, i / 32))).join(', ')})`;
  return (
    <div className="lut-range">
      <div className="lut-bar" style={{ background: full }}>
        <div className="lut-range-out" style={{ left: 0, width: `${a * 100}%` }} />
        <div className="lut-range-out" style={{ right: 0, width: `${(1 - b) * 100}%` }} />
      </div>
      <Slider label="Start of the LUT used" value={a} max={0.95} step={0.01} reset={0}
        onChange={v => onChange([Math.min(v, b - 0.05), b])} />
      <Slider label="End of the LUT used" value={b} min={0.05} max={1} step={0.01} reset={1}
        onChange={v => onChange([a, Math.max(v, a + 0.05)])} />
    </div>
  );
}

// Image couleur (HES…) : affichée telle quelle ; se règlent la visibilité, l'opacité et la saturation.
function RgbChannelCard({ channel, onChange, onDelete, onRename, collapsed, onCollapse }) {
  const { name, visible, opacity, saturation } = channel;
  return (
    <div className={`channel${visible ? '' : ' off'}${collapsed ? ' collapsed' : ''}`}>
      <div className="channel-header">
        <span className="swatch rgb" title="Color image" />
        <CardTitle name={name} collapsed={collapsed} onCollapse={onCollapse} onRename={onRename} />
        {onDelete && <DeleteButton name={name} onDelete={onDelete} />}
        {/* HE toujours affiché (pas d'interrupteur) ; les autres modalités se comparent à lui. */}
        {!channel.alwaysOn && (
          <label className="switch" title="Show / hide">
            <input type="checkbox" checked={visible} onChange={e => onChange({ visible: e.target.checked })} />
            <span />
          </label>
        )}
      </div>
      {!collapsed && (
        <div className="channel-body">
          <Slider label="Opacity" value={opacity} max={1} reset={1} onChange={v => onChange({ opacity: v })} />
          <Slider label="Saturation" value={saturation} max={2} reset={1} onChange={v => onChange({ saturation: v })} />
        </div>
      )}
    </div>
  );
}

// Réglages d'une modalité : visibilité, couleur, histogramme + contraste (min / max), opacité, flou, LUT, inversion.
// onDelete (auteur de l'image ou admin) : bouton de suppression définitive.
// collapsed / onCollapse : carte repliée (en-tête seul) et bascule. locked (présentation) : couleur non modifiable.
export default function ChannelCard(props) {
  return props.channel.rgb ? <RgbChannelCard {...props} /> : <GrayChannelCard {...props} />;
}

function GrayChannelCard({ channel, onChange, onDelete, onRename, collapsed, onCollapse, locked }) {
  const { name, color, lut, inverted, visible, contrast, domain, histogram, lutRange } = channel;
  const original = channel.toOriginal || (v => v);
  const [lo, hi] = domain;
  const [min, max] = contrast;
  const step = Math.max(1, Math.round((hi - lo) / 1000));
  const pct = v => ((v - lo) / (hi - lo || 1)) * 100;
  const isColor = lut === 'color';
  const accent = isColor ? color : lutColor(lut, color, false, 0.8);
  const histoRef = useRef(null);
  const lutRef = useRef(null);

  // Histogramme (échelle log : le fond domine souvent) et barre de LUT alignée dessous ; redessinés au dépliage.
  useEffect(() => {
    if (collapsed) return undefined;
    const draw = () => {
      const hctx = fitCanvas(histoRef.current);
      const peak = Math.log1p(Math.max(...histogram));
      const W = hctx.canvas.width, H = hctx.canvas.height, bw = W / histogram.length;
      hctx.clearRect(0, 0, W, H);
      hctx.fillStyle = css(accent);
      hctx.globalAlpha = 0.85;
      histogram.forEach((n, b) => {
        const h = (Math.log1p(n) / peak) * H;
        hctx.fillRect(b * bw, H - h, Math.ceil(bw), h);
      });
      const lctx = fitCanvas(lutRef.current);
      for (let x = 0; x < lctx.canvas.width; x++) {
        const v = lo + (x / lctx.canvas.width) * (hi - lo);
        lctx.fillStyle = css(lutColor(lut, color, inverted, (v - min) / (max - min || 1), lutRange));
        lctx.fillRect(x, 0, 1, lctx.canvas.height);
      }
    };
    draw();
    window.addEventListener('resize', draw);
    return () => window.removeEventListener('resize', draw);
  }, [collapsed, histogram, accent, lut, color, inverted, min, max, lo, hi, lutRange?.[0], lutRange?.[1]]);

  const setLimit = (which, value) => {
    const v = Number(value);
    onChange({ contrast: which === 'min' ? [Math.min(v, max), max] : [min, Math.max(v, min)] });
  };

  return (
    <div className={`channel${visible ? '' : ' off'}${collapsed ? ' collapsed' : ''}`}>
      <div className="channel-header">
        <label className={`swatch${isColor && !locked ? '' : ' disabled'}`} style={{ background: css(accent) }}
          title={isColor ? (locked ? undefined : 'Channel color') : 'Color set by the LUT'}>
          <input type="color" value={rgbToHex(color)} onChange={e => onChange({ color: hexToRgb(e.target.value) })} />
        </label>
        <CardTitle name={name} collapsed={collapsed} onCollapse={onCollapse} onRename={onRename} />
        {onDelete && <DeleteButton name={name} onDelete={onDelete} />}
        <label className="switch" title="Show / hide">
          <input type="checkbox" checked={visible} onChange={e => onChange({ visible: e.target.checked })} />
          <span />
        </label>
      </div>
      {!collapsed && (
        <div className="channel-body">
          <div className="histo">
            <canvas ref={histoRef} />
            <div className="histo-mask lo" style={{ width: `${pct(min)}%` }} />
            <div className="histo-mask hi" style={{ width: `${100 - pct(max)}%` }} />
          </div>
          <canvas className="lut-bar" ref={lutRef} />
          <div className="range">
            <div className="range-fill" style={{ left: `${pct(min)}%`, width: `${pct(max) - pct(min)}%` }} />
            <input type="range" min={lo} max={hi} step={step} value={min} aria-label="Contrast min"
              onChange={e => setLimit('min', e.target.value)} />
            <input type="range" min={lo} max={hi} step={step} value={max} aria-label="Contrast max"
              onChange={e => setLimit('max', e.target.value)} />
          </div>
          {/* Images en tuiles 8 bits : valeurs affichées dans l'unité d'origine du fichier (16 bits en général). */}
          <div className="range-values"><span>{Math.round(original(min))}</span><span>{Math.round(original(max))}</span></div>
          <div className="channel-opacity">
            <Slider label="Opacity" value={channel.opacity ?? 1} max={1} reset={1} onChange={v => onChange({ opacity: v })} />
          </div>
          {/* Flou gaussien (src/components/Viewer/blur.js) : écart-type en px pleine résolution. */}
          <div className="channel-opacity">
            <Slider label="Blur (σ, full-resolution px)" value={channel.blur ?? 0} max={40} step={0.5} reset={0}
              format={v => (v ? `σ ${v.toLocaleString('en-US')} px` : 'none')} onChange={v => onChange({ blur: v })} />
          </div>
          <div className="channel-options">
            <select value={lut} aria-label="LUT" onChange={e => onChange({ lut: e.target.value })}>
              <option value="color">{lutLabel('color')}</option>
              {LUT_GROUPS.map(group => (
                <optgroup key={group.label} label={group.label}>
                  {group.ids.map(id => <option key={id} value={id}>{lutLabel(id)}</option>)}
                </optgroup>
              ))}
            </select>
            <label className={`chip${inverted ? ' checked' : ''}`}>
              <input type="checkbox" checked={inverted} onChange={e => onChange({ inverted: e.target.checked })} />
              Invert
            </label>
          </div>
          {!isColor && <LutRange lut={lut} range={lutRange} onChange={r => onChange({ lutRange: r })} />}
        </div>
      )}
    </div>
  );
}
